/* eslint-disable react/forbid-prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { isArraysEqual } from '../../helpers/utils';
import SelectedIcon from '../../pictures/icon-selected.svg';
import ArrowIcon from '../../pictures/icon-arrow.svg';

import ActionIcon from '../ActionIcon';

import * as style from './SelectMulti.module.scss';

function SelectMulti({
  style: customStyle = {},
  value = [],
  handleChange,
  handleClickOption,
  placeholder,
  list,
  name,
  handleToggleOptions,
  component,
  disabled,
  type,
  error,
  id,
}) {
  const ref = useRef(null);

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(value);

  const toggleOptions = () => {
    setIsOptionsOpen(!isOptionsOpen);
    handleToggleOptions && handleToggleOptions();
  };

  const handleOptionClick = (index) => {
    const optionValue = list[index]?.value;
    let newSelectedOptions;

    if (selectedOptions.includes(optionValue)) {
      newSelectedOptions = selectedOptions.filter((val) => val !== optionValue);
    } else {
      newSelectedOptions = [...selectedOptions, optionValue];
    }

    newSelectedOptions.sort(); // Sort the selected options
    setSelectedOptions(newSelectedOptions);
    handleChange(name, newSelectedOptions);
  };

  const handleClickOutside = (event) => {
    if (ref && ref.current && !ref.current.contains(event.target)) {
      setIsOptionsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (!isArraysEqual(value, selectedOptions)) {
      setSelectedOptions(value);
    }
  }, [value]);

  const handleKeyDown = (index) => (e) => {
    switch (e.key) {
      case ' ':
      case 'SpaceBar':
      case 'Enter':
        e.preventDefault();
        handleOptionClick(index);
        break;
      default:
        break;
    }
  };

  const handleListKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOptionsOpen(false);
        break;
      case 'ArrowUp':
        e.preventDefault();
        // Handle arrow up key
        break;
      case 'ArrowDown':
        e.preventDefault();
        // Handle arrow down key
        break;
      default:
        break;
    }
  };

  const onRemoveOption = (option) => {
    const newSelectedOptions = selectedOptions.filter((val) => val !== option.value);
    newSelectedOptions.sort(); // Sort the selected options
    setSelectedOptions(newSelectedOptions);
    handleChange(name, newSelectedOptions);
  }

  return (
    <div
      className={cn(style.select, {
        [style.active]: isOptionsOpen,
        [style.error]: error,
        [style.text]: type === 'text',
        [style.textColor]: type === 'textColor',
      })}
      style={customStyle}
      id={id}
    >
      <div
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOptionsOpen}
        className={cn(
          style.button1, {
            'expanded': isOptionsOpen,
          }
        )}
        onClick={toggleOptions}
        onKeyDown={handleListKeyDown}
        disabled={disabled}
      >
        {
          selectedOptions.length === 0
            ? <span className={style.placeholder}>{placeholder}</span>
            : (
              <div className={style.selectedTitles}>
                {selectedOptions.map((selectedValue) => {
                  const selectedOption = list.find((option) => option.value === selectedValue);
                  return (
                    <div key={selectedValue} className={style.selectedChip}>
                      <span>{selectedOption?.title}</span>
                      <div className={style.closeGroup}>
                        <ActionIcon
                            icon="close-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveOption(selectedOption)
                            }}
                            extraSmall
                            style={{ pading: '2px'}}
                          />
                      </div>
                    </div>
                  );
                })}
              </div>
            )
        }
        {!disabled && (
          <div className={style.selectAngle}>
            <ArrowIcon />
          </div>
        )}
      </div>
      <ul
        className={cn(style.options, {
          [style.show]: isOptionsOpen,
          [style.top]: type === 'top',
        })}
        role="listbox"
        aria-activedescendant={list && selectedOptions}
        tabIndex={-1}
        onKeyDown={handleListKeyDown}
        ref={ref}
      >
        {list?.map((option, index) => (option.component ? (
          <li
            key={option.value}
            role="option"
            onClick={() => {
              if (handleClickOption) handleClickOption(option.value);
              setIsOptionsOpen(false);
            }}
          >
            {option.component}
          </li>
        ) : (
          <li
            className={cn({
              [style.delete]: option.red,
              [style.hidden]: option.hidden,
            })}
            key={option.value}
            role="option"
            aria-selected={selectedOptions.includes(option.value)}
            tabIndex={0}
            onKeyDown={handleKeyDown(index)}
            onClick={() => {
              if (option.disabled) return;
              handleOptionClick(index);
            }}
            disabled={option.disabled}
          >
            {option.icon}
            <span>{option.title}</span>
            {selectedOptions.includes(option.value) && (
              <div className={style.iconSelected}>
                <SelectedIcon />
              </div>
            )}
          </li>
        )))}
      </ul>
    </div>
  );
}

SelectMulti.propTypes = {
  type: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  handleChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  list: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.object,
  })),
  disabled: PropTypes.bool,
  style: PropTypes.object,
  id: PropTypes.string,
};

export default SelectMulti;
